import { itemDef, items } from '../data/items';
import { mulberry32, seedAt, weightedPick, randInt } from '../core/Rng';
import type { World } from '../world/World';
import type { Player } from '../player/Player';

/**
 * Ekonomi: BEDO'nun dükkânı ve fiyatlandırma.
 *
 * Fiyatlar item verisindeki `value` alanından türetilir; zorluk `shopPrice`
 * çarpanı alışı pahalı, satışı ucuz yapar. Stok her OYUN GÜNÜ yenilenir ve
 * (seed + gün) ile deterministiktir: aynı gün tekrar girildiğinde aynı stok.
 */

export interface StockEntry {
  item: string;
  count: number;
  price: number;
}

/** Satış fiyatı (oyuncu -> BEDO). */
export function sellPrice(world: World, itemId: string): number {
  const def = itemDef(itemId);
  if (def.value <= 0) return 0;
  // Zor modda tüccar daha az öder
  return Math.max(1, Math.round(def.value * 0.55 * (2 - world.params.difficulty.shopPrice)));
}

/** Alış fiyatı (BEDO -> oyuncu). */
export function buyPrice(world: World, itemId: string): number {
  const def = itemDef(itemId);
  return Math.max(1, Math.round(def.value * 1.6 * world.params.difficulty.shopPrice));
}

/**
 * Günlük stok.
 * Temel kaynaklar sık, nadir madenler çok seyrek çıkar (tasarım gereği).
 */
export function dailyStock(world: World, day: number): StockEntry[] {
  const rand = mulberry32(seedAt(world.params.seed, day, 7331, 5));
  const pool = items
    .filter((i) => i.value > 0 && i.kind !== 'fish' && i.id !== 'kug_sword' && !i.id.startsWith('power_'))
    .map((i) => {
      // Nadirlik arttıkça ağırlık düşer; alet/zırh orta sıklıkta
      const rarityWeight = { common: 100, uncommon: 40, rare: 10, veryRare: 2.5, legendary: 0.4 }[i.rarity];
      const kindWeight = i.kind === 'material' ? 1.4 : i.kind === 'food' ? 1.1 : i.kind === 'potion' ? 0.7 : 0.6;
      return { weight: rarityWeight * kindWeight, value: i };
    });

  const out: StockEntry[] = [];
  const seen = new Set<string>();
  // Sabit sayıda kalem: liste her gün değişsin ama boyutu tahmin edilebilir olsun
  for (let i = 0; i < 40 && out.length < 8; i++) {
    const pick = weightedPick(rand, pool);
    if (!pick || seen.has(pick.id)) continue;
    seen.add(pick.id);
    const maxCount = pick.kind === 'material' ? 24 : pick.kind === 'food' ? 8 : 1;
    out.push({
      item: pick.id,
      count: Math.max(1, randInt(rand, 1, maxCount)),
      price: buyPrice(world, pick.id),
    });
  }
  return out;
}

export type TradeResult = { ok: true; message: string } | { ok: false; reason: string };

export function buy(_world: World, player: Player, entry: StockEntry, count = 1): TradeResult {
  const n = Math.max(1, Math.min(count, entry.count));
  const cost = entry.price * n;
  if (player.kogox < cost) return { ok: false, reason: `Yetersiz Kogox (${cost} gerekiyor)` };
  const stack = itemDef(entry.item).stack;
  if (stack === 1 && player.inventory.freeSlots() < n) return { ok: false, reason: 'Çantada yer yok' };
  const left = player.inventory.add(entry.item, n);
  if (left > 0) {
    player.inventory.remove(entry.item, n - left);
    return { ok: false, reason: 'Çantada yer yok' };
  }
  player.kogox -= cost;
  entry.count -= n;
  return { ok: true, message: `${itemDef(entry.item).name} x${n} alındı (-${cost} Kogox)` };
}

export function sell(world: World, player: Player, itemId: string, count = 1): TradeResult {
  const have = player.inventory.count(itemId);
  if (have <= 0) return { ok: false, reason: 'Bu eşya yok' };
  const n = Math.min(count, have);
  const unit = sellPrice(world, itemId);
  if (unit <= 0) return { ok: false, reason: 'Bu eşya satılamıyor' };
  if (!player.inventory.remove(itemId, n)) return { ok: false, reason: 'Eşya düşülemedi' };
  const gain = unit * n;
  player.kogox += gain;
  return { ok: true, message: `${itemDef(itemId).name} x${n} satıldı (+${gain} Kogox)` };
}

/** Satılabilir envanter kalemleri (UI listesi). */
export function sellableItems(world: World, player: Player): Array<{ item: string; count: number; unit: number }> {
  const map = new Map<string, number>();
  for (const slot of player.inventory.slots) {
    if (!slot) continue;
    map.set(slot.item, (map.get(slot.item) ?? 0) + slot.count);
  }
  return [...map]
    .map(([item, count]) => ({ item, count, unit: sellPrice(world, item) }))
    .filter((e) => e.unit > 0)
    .sort((a, b) => b.unit * b.count - a.unit * a.count);
}
